import React, {useEffect, useRef, useState} from 'react';
import '../styles/EquationDisplay.css';
import {useTranslation} from "react-i18next";
import {trackEvent} from '../analytics';

function EquationDisplay({equations, onFinish, groupSize, sessionSummary}) {
    const [currentGroupIndex, setCurrentGroupIndex] = useState(0);
    const [answers, setAnswers] = useState(equations.map(() => ''));
    const inputRefs = useRef(equations.map(() => React.createRef()));
    const answeredCount = answers.filter((answer) => answer !== '' && !Number.isNaN(answer)).length;
    const progress = equations.length ? Math.round((answeredCount / equations.length) * 100) : 0;
    const totalGroups = Math.ceil(equations.length / groupSize);

    // track session start time
    const startTimeRef = useRef(Date.now());

    // Helper to ensure an input is visible when keyboard opens
    const scrollInputIntoView = (inputEl) => {
        if (!inputEl) return;
        try {
            // Delay to allow virtual keyboard and layout to settle
            setTimeout(() => {
                inputEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 50);
        } catch (_) {
            // no-op
        }
    };

    useEffect(() => {
        // Focus the first input field of the current group
        const firstInputIndex = currentGroupIndex * groupSize;
        const ref = inputRefs.current[firstInputIndex];
        if (ref && ref.current) {
            ref.current.focus();
            scrollInputIntoView(ref.current);
        }
    }, [currentGroupIndex, groupSize]);

    const handleAnswerChange = (e, index) => {
        const updatedAnswers = [...answers];
        updatedAnswers[index] = parseInt(e.target.value);
        setAnswers(updatedAnswers);
    };

    const handleAnswerKeyUp = (e, index) => {
        if (e.key === 'Enter') {
            if ((index + 1) >= equations.length) {
                handleFinish();
                return;
            }
            if ((index + 1) % groupSize === 0) {
                goToNextGroup();
                return;
            }
            const nextRef = inputRefs.current[index + 1];
            if (nextRef && nextRef.current) {
                nextRef.current.focus();
                scrollInputIntoView(nextRef.current);
            }
        }
        if (e.key === 'Escape') {
            handleFinish();
        }
    };

    const goToNextGroup = () => {
        setCurrentGroupIndex(currentGroupIndex + 1);
    };

    const goToPreviousGroup = () => {
        if (currentGroupIndex > 0) {
            setCurrentGroupIndex(currentGroupIndex - 1);
        }
    };

    const {t} = useTranslation();

    const handleFinish = () => {
        const durationMs = Date.now() - startTimeRef.current;
        const answeredCount = answers.filter(a => a !== '' && !Number.isNaN(a)).length;
        trackEvent('session_finished', {
            totalEquations: equations.length,
            answeredCount,
            groupsVisited: currentGroupIndex + 1,
            groupSize,
            durationSec: Math.round(durationMs / 1000)
        });
        onFinish(answers);
    };

    return (
        <div className="equation-display">
            <section className="session-ribbon" aria-label="Practice progress">
                <div>
                    <span className="session-label">{sessionSummary?.operationsLabel}</span>
                    <strong>{answeredCount}/{equations.length}</strong>
                </div>
                <div className="progress-track" aria-hidden="true">
                    <span style={{width: `${progress}%`}}/>
                </div>
                <div className="session-meta">
                    <span>{t('equations.group')} {currentGroupIndex + 1}/{totalGroups}</span>
                    <span>{progress}%</span>
                </div>
            </section>
            <div className="equation-container">
                {equations.slice(currentGroupIndex * groupSize, (currentGroupIndex + 1) * groupSize).map((equation, index) => (
                    <div key={index} className="equation">
                        <label>
                            {equation}
                            <input
                                type="number"
                                value={answers[currentGroupIndex * groupSize + index]}
                                onChange={(e) => handleAnswerChange(e, currentGroupIndex * groupSize + index)}
                                ref={inputRefs.current[currentGroupIndex * groupSize + index]}
                                onKeyUp={(e) => handleAnswerKeyUp(e, currentGroupIndex * groupSize + index)}
                                onFocus={(e) => scrollInputIntoView(e.target)}
                            />
                        </label>
                    </div>
                ))}
            </div>
            <div className="button-container">
                <button onClick={goToPreviousGroup} disabled={currentGroupIndex === 0}
                        className="primary-button">{t('equations.previous')}</button>
                <button onClick={goToNextGroup}
                        disabled={currentGroupIndex === Math.ceil(equations.length / groupSize) - 1}
                        className="primary-button">{t('equations.next')}</button>
                <button onClick={handleFinish} className="primary-button">{t('equations.finish')}</button>
            </div>
        </div>
    );
}

export default EquationDisplay;
